import React from "react"
import PropTypes from "prop-types"
import { Flex, Skeleton } from "antd"
import { Text } from "ui/Typography"
import { Icon } from "ui/Icon"
import styles from "./BlackoutSectionCard.module.css"

/**
 * The card shell shared by the Business, Listing and Resources sections: heading,
 * description, an action slot on the right, and either the empty state or `children`.
 *
 * `variant` picks the ground the section sits on — "plain" is the white bordered card used by
 * Business, "surface" the tinted block whose rows carry the white cards instead.
 */
const BlackoutSectionCard = ({ title, description, action, isEmpty, emptyText, isLoading, subHeader, variant = "plain", children, className }) => (
  <section className={`${styles.card} ${styles[variant]} ${className || ""}`}>
    <Flex align="flex-start" justify="space-between" gap="var(--spacing-m)" className={styles.header}>
      <div className={styles.heading}>
        <Text variant="sub" size="lg" fw="600" className={styles.title}>
          {title}
        </Text>
        {description && (
          <Text variant="caption" size="lg" className={styles.description}>
            {description}
          </Text>
        )}
      </div>
      {action}
    </Flex>

    {subHeader}

    {isLoading && <Skeleton active paragraph={{ rows: 3 }} title={false} className={styles.skeleton} />}

    {!isLoading && isEmpty && (
      <Flex vertical align="center" justify="center" gap="var(--spacing-s)" className={styles.emptyState}>
        <span className={styles.emptyIcon}>
          <Icon name="loungeCalendar" width={60} height={49.5} />
        </span>
        <Text variant="caption" size="lg" className={styles.emptyText}>
          {emptyText}
        </Text>
      </Flex>
    )}

    {!isLoading && !isEmpty && <div className={styles.body}>{children}</div>}
  </section>
)

BlackoutSectionCard.propTypes = {
  title: PropTypes.node.isRequired,
  description: PropTypes.node,
  action: PropTypes.node,
  isEmpty: PropTypes.bool,
  emptyText: PropTypes.node,
  isLoading: PropTypes.bool,
  subHeader: PropTypes.node,
  variant: PropTypes.oneOf(["plain", "surface"]),
  children: PropTypes.node,
  className: PropTypes.string
}

export default BlackoutSectionCard
